import { z } from 'zod';

/**
 * Zod schemas for the skill SSE stream (server -> browser) -- the single
 * source of truth shared by the server emitter
 * (`src/services/skills/sdk/sse-writer.ts`) and the web consumer
 * (`web/app-home/src/lib/skill-stream-schemas.ts`, which re-exports this).
 */

export const DeltaPayloadSchema = z.object({
  text: z.string(),
});

export const ToolStartPayloadSchema = z.object({
  name: z.string(),
  input: z.unknown(),
  id: z.string().optional(),
});

export const ToolEndPayloadSchema = z.object({
  name: z.string(),
  status: z.enum(['success', 'error']),
  preview: z.string().optional(),
  result: z.unknown().optional(),
  id: z.string().optional(),
});

// No duration field — a turn's real duration comes from its sibling `timing` event, not this one.
export const CompletePayloadSchema = z.object({
  output: z.unknown(),
  stats: z.object({
    turns: z.number(),
    toolCalls: z.number(),
  }),
});

export const ErrorPayloadSchema = z.object({
  code: z.enum(['INTERNAL', 'ABORTED', 'RATE_LIMITED', 'AUTH_FAILED']),
  message: z.string(),
});

/**
 * Snapshot of what the agent loop built up before sending to the model.
 * Emitted once at the start of a run when the surface wants a debug view
 * (e.g. SkillEditor / PersonaEditor preview). Lets the user answer "did
 * the model actually see my artifact_context? did my composeFields take
 * effect?" without instrumenting the network.
 *
 * Schemas in `tools` are kept loose (`unknown`) — the JSON Schema shape
 * Anthropic carries is whatever the registry produced (including narrowed
 * compose_form_field enums). Tightening it to a Zod schema would mean
 * mirroring Anthropic's tool-schema type, which churns with the SDK.
 */
export const RequestSnapshotPayloadSchema = z.object({
  system: z.string(),
  messages: z.array(
    z.object({
      role: z.enum(['user', 'assistant']),
      content: z.unknown(),
    }),
  ),
  tools: z.array(
    z.object({
      name: z.string(),
      description: z.string().optional(),
      input_schema: z.unknown().optional(),
    }),
  ),
  composeFields: z.array(z.string()).readonly().optional(),
});

export type DeltaPayload = z.infer<typeof DeltaPayloadSchema>;
export type ToolStartPayload = z.infer<typeof ToolStartPayloadSchema>;
export type ToolEndPayload = z.infer<typeof ToolEndPayloadSchema>;
export type CompletePayload = z.infer<typeof CompletePayloadSchema>;
export type ErrorPayload = z.infer<typeof ErrorPayloadSchema>;
export type RequestSnapshotPayload = z.infer<typeof RequestSnapshotPayloadSchema>;

/**
 * Event-name → schema lookup. Central dispatch keeps the onmessage
 * switch statement from needing to know which schema pairs with
 * which event.
 */
export const SKILL_STREAM_SCHEMAS = {
  delta: DeltaPayloadSchema,
  tool_start: ToolStartPayloadSchema,
  tool_end: ToolEndPayloadSchema,
  complete: CompletePayloadSchema,
  error: ErrorPayloadSchema,
  request_snapshot: RequestSnapshotPayloadSchema,
} as const;

export type SkillStreamEventName = keyof typeof SKILL_STREAM_SCHEMAS;

/** Type guard: is the wire event name one of the known skill-stream events? */
export function isSkillStreamEventName(name: string): name is SkillStreamEventName {
  return Object.prototype.hasOwnProperty.call(SKILL_STREAM_SCHEMAS, name);
}
